"use client";

import React, { useState } from "react";

export interface VideoCredit {
  /** e.g. "UMMG Documentary Team" */
  team?: string;
  /** e.g. ["Pe Maung Thu", "Chit Myo Ko", …] — joined with • */
  members?: string[];
  /** e.g. "Medical Year 4 (5/2026)" */
  note?: string;
}

export interface VideoItem {
  /** 11-char YouTube id, pre-parsed server-side (or the file URL for `src`). */
  id: string;
  /** Self-hosted media file — played with an HTML5 <video> instead of YouTube. */
  src?: string;
  /** Poster frame for a `src` video. */
  poster?: string;
  title: string;
  credit?: VideoCredit;
}

interface Props {
  videos: VideoItem[];
  /** Localized "Watch" / play affordance label (a11y). */
  playLabel?: string;
}

/**
 * Client widget: a large featured player (YouTube embed or HTML5 <video>
 * for a self-hosted file) + a rail of thumbnails. Clicking a thumbnail swaps
 * the featured player (and autoplays it, since the swap is a user gesture).
 * The first video is featured on load and does NOT autoplay. YouTube
 * thumbnails come from YouTube; files use their `poster`, else first frame.
 * Shared by every theme — see [[VideoFeedSection]].
 */
export default function VideoFeedClient({ videos, playLabel = "Play" }: Props) {
  const [active, setActive] = useState(0);
  // Tracks whether the current featured video was chosen by a click, so we
  // only autoplay after an explicit user gesture (never on first paint).
  const [interacted, setInteracted] = useState(false);

  if (!videos || videos.length === 0) return null;
  const idx = Math.min(Math.max(0, active), videos.length - 1);
  const current = videos[idx];
  const src =
    `https://www.youtube-nocookie.com/embed/${current.id}` +
    `?rel=0&modestbranding=1${interacted ? "&autoplay=1" : ""}`;

  return (
    <div className={`grid gap-5 ${videos.length > 1 ? "lg:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
      {/* Featured player */}
      <div>
        <div className="relative w-full overflow-hidden rounded-xl bg-black shadow-lg ring-1 ring-black/10 aspect-video">
          {current.src ? (
            <video
              key={current.id}
              src={current.src}
              poster={current.poster}
              title={current.title}
              className="absolute inset-0 h-full w-full bg-black object-contain"
              controls
              playsInline
              preload="metadata"
              autoPlay={interacted}
            />
          ) : (
            <iframe
              key={current.id}
              src={src}
              title={current.title}
              className="absolute inset-0 h-full w-full"
              allow="accelerated-encoders; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          )}
        </div>
        {current.title ? (
          <h3 className="mt-3 text-base font-semibold leading-snug text-foreground md:text-lg">
            {current.title}
          </h3>
        ) : null}
        {current.credit &&
        (current.credit.team ||
          current.credit.members?.length ||
          current.credit.note) ? (
          <figcaption className="mt-3 border-l-2 border-primary/50 pl-3">
            {current.credit.team ? (
              <div className="text-[13px] font-semibold text-foreground">
                {current.credit.team}
              </div>
            ) : null}
            {current.credit.members?.length ? (
              <div className="mt-0.5 text-[13px] text-muted-foreground">
                {current.credit.members.join("  •  ")}
              </div>
            ) : null}
            {current.credit.note ? (
              <div className="mt-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {current.credit.note}
              </div>
            ) : null}
          </figcaption>
        ) : null}
      </div>

      {/* Thumbnail rail — vertical scroll on desktop, horizontal on mobile */}
      {videos.length > 1 ? (
        <ul className="flex gap-3 overflow-x-auto pb-2 lg:max-h-[420px] lg:flex-col lg:overflow-y-auto lg:overflow-x-visible lg:pb-0 lg:pr-1">
          {videos.map((v, i) => {
            const isActive = i === idx;
            return (
              <li key={v.id + i} className="shrink-0 lg:shrink">
                <button
                  type="button"
                  onClick={() => {
                    setActive(i);
                    setInteracted(true);
                  }}
                  aria-label={`${playLabel}: ${v.title}`}
                  aria-current={isActive}
                  className={`group flex w-[220px] gap-3 rounded-lg p-1.5 text-left transition lg:w-full ${
                    isActive
                      ? "bg-primary/10 ring-1 ring-primary/40"
                      : "hover:bg-muted"
                  }`}
                >
                  <span className="relative block aspect-video w-[110px] shrink-0 overflow-hidden rounded-md bg-muted">
                    {v.src && !v.poster ? (
                      // No poster → let the browser paint the file's first frame.
                      <video
                        src={`${v.src}#t=0.1`}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={v.src ? v.poster : `https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full transition ${
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-black/55 text-white group-hover:bg-primary group-hover:text-primary-foreground"
                        }`}
                      >
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  </span>
                  <span className="min-w-0 flex-1 self-center">
                    <span
                      className={`line-clamp-2 text-[13px] font-medium leading-snug ${
                        isActive ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {v.title}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
